import React from 'react';
import PropTypes from 'prop-types';

import {
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';

import {
  bodyCellSx,
  bodyRowSx,
  headCellSx,
  paginationSx,
  tableContainerSx,tablePaperSx
} from './styles';

const CommonTable = ({
  columns,
  rows,
  totalCount,
  page,
  rowsPerPage,
  rowsPerPageOptions,
  emptyText,
  loading,
  skeletonRows,
  onPageChange,
  onRowsPerPageChange,
  onActionClick,
  renderAction,
  actionWrapper
}) => {
 return (
  <Paper
    elevation={0}
    sx={{...tablePaperSx,
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
      width: '100%'
    }}>
    <TableContainer
      sx={{
        ...tableContainerSx,
        flex: 1,
        minHeight: 0,
        overflowX: 'auto',
        overflowY: 'auto'
      }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: 'fixed',
          width: '100%'
        }}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  ...headCellSx,
                  width: column.width
                }}>
                {column.headerName}
              </TableCell>
            ))}

            {renderAction && (
              <TableCell
                align="center"
                sx={{
                  ...headCellSx,
                  width: 90
                }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading &&
            Array.from({ length: skeletonRows }).map(
              (_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`}>
                  {columns.map(column => (
                    <TableCell
                      key={column.field}
                      sx={bodyCellSx}>
                      <Skeleton
                        width={
                          column.skeletonWidth || '60%'
                        }
                      />
                    </TableCell>
                  ))}

                  {renderAction && (
                    <TableCell
                      align="center"
                      sx={bodyCellSx}>
                      <Skeleton
                        variant="circular"
                        width={22}
                        height={22}
                        sx={{ mx: 'auto' }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              )
            )}

          {!loading &&
            rows.map((row, rowIndex) => (
              <TableRow
                key={
                  row.id ||
                  `${row.patient || row.pname}-${rowIndex}`
                }
                sx={bodyRowSx}>
                {columns.map(column => {
                  const value = row[column.field];

                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      sx={{
                        ...bodyCellSx,
                        ...(column.cellSx
                          ? column.cellSx(value, row)
                          : {}),
                        fontWeight: column.primary
                          ? 500
                          : 400
                      }}>
                      {column.renderCell
                        ? column.renderCell(value, row)
                        : value}
                    </TableCell>
                  );
                })}

                {renderAction && (
                  <TableCell
                    align="center"
                    sx={bodyCellSx}>
                    {actionWrapper ? (
                      <IconButton
                        size="small"
                        onClick={() =>
                          onActionClick?.(row)
                        }
                        sx={{
                          p: 0.5,
                          color: '#757575',

                          '&:hover': {
                            color: '#1976D2',
                            bgcolor: 'transparent'
                          }
                        }}>
                        {renderAction(row)}
                      </IconButton>
                    ) : (
                      renderAction(row)
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}

          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={
                  columns.length +
                  (renderAction ? 1 : 0)
                }
                align="center"
                sx={{
                  py: 4,
                  borderBottom: 0
                }}>
                <Typography
                  sx={{
                    color: '#757575',
                    fontSize: 13
                  }}>
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>

    {!loading && (
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        labelRowsPerPage="Rows per page:"
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          ...paginationSx,
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '1px solid #E0E0E0'
        }}
      />
    )}
  </Paper>
);
};

CommonTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      align: PropTypes.string,
      primary: PropTypes.bool,
      renderCell: PropTypes.func,
      cellSx: PropTypes.func,
      skeletonWidth: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ])
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  emptyText: PropTypes.string,
  loading: PropTypes.bool,
  skeletonRows: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onActionClick: PropTypes.func,
  renderAction: PropTypes.func,
  actionWrapper: PropTypes.bool
};

CommonTable.defaultProps = {
  rowsPerPageOptions: [25, 50, 75, 100],
  emptyText: 'No records found.',
  loading: false,
  skeletonRows: 10,
  onActionClick: undefined,
  renderAction: undefined,
  actionWrapper: true
};

export default CommonTable;